import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from 'react';
// Verzögert geladen: IndustryPage führt über LayoutDialog zu navBuilder und damit zur Modulliste, die ihrerseits dieses Modul importiert. Ein direkter Import schlösse den Kreis, und
// die Seitenleiste verlöre die Pfade von Angebote, Kontakte, Produkte und Dokumente.
const IndustryPage = lazy(() => import('../../pages/IndustryPage').then((m) => ({ default: m.IndustryPage })));
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_OFFER_TEXTS, DEFAULT_STATE, FINANCE_HINTS, GERMAN_STATE_CODES, GERMAN_STATES, type CompanyProfile, type CompanyProfileInput, type GermanState } from '@kontor/shared';
import { api, ApiError, apiUpload } from '../../api';
import { useSession } from '../../session';
import './offers.css';

type Texts = Record<Exclude<keyof CompanyProfileInput, 'smallBusiness' | 'offerValidityDays' | 'state'>, string>;
const KEYS: (keyof Texts)[] = [
  'companyName', 'street', 'postalCode', 'city', 'phone', 'email', 'website', 'managingDirector', 'registerCourt', 'registerNumber',
  'taxNumber', 'vatId', 'bankName', 'iban', 'bic', 'footerNote', 'taxAdvisorName', 'taxAdvisorEmail', 'datevConsultantNumber', 'datevClientNumber',
  'offerIntro', 'offerClosing', 'mailSubject', 'mailBody',
];
const empty = Object.fromEntries(KEYS.map((k) => [k, ''])) as Texts;

/**
 * Die Firmendaten sind in Schritte aufgeteilt, damit man sich auf einen Bereich konzentrieren kann. Die Felder je Schritt zählen für die Anzeige „x von y ausgefüllt“
 * und für den Sprung zum ersten Schritt mit einem Fehler beim Speichern.
 */
const STEPS: { key: string; label: string; hint: string; fields: (keyof Texts | 'offerValidityDays')[] }[] = [
  { key: 'company', label: 'Firma', hint: 'Name, Anschrift und Kontakt. Daraus entsteht der Briefkopf.', fields: ['companyName', 'street', 'postalCode', 'city', 'phone', 'email', 'website'] },
  { key: 'legal', label: 'Rechtliches', hint: 'Angaben, die auf Rechnungen und Angeboten stehen müssen.', fields: ['managingDirector', 'registerCourt', 'registerNumber', 'taxNumber', 'vatId'] },
  { key: 'bank', label: 'Bank und Steuerberater', hint: 'Bankverbindung für die Überweisung, dazu Ihre Kanzlei für die Übergabe der Buchungen.', fields: ['bankName', 'iban', 'bic', 'taxAdvisorName', 'taxAdvisorEmail', 'datevConsultantNumber', 'datevClientNumber'] },
  { key: 'offers', label: 'Angebote', hint: 'Gültigkeit, Texte im Angebot und die E-Mail beim Versand.', fields: ['offerValidityDays', 'offerIntro', 'offerClosing', 'mailSubject', 'mailBody'] },
];

/**
 * Bereich „Firmendaten“ der Einrichtung (KON-097). Daraus entstehen Briefkopf und Fußzeile des Angebots-PDF, die Vorgabetexte und die E-Mail beim Versand.
 * Ein abgeschlossenes Angebot behält die Firmendaten von damals, eine Änderung hier betrifft nur neue Angebote.
 */
export function CompanyProfilePage() {
  const { can } = useSession();
  const canWrite = can('setup.write');
  const client = useQueryClient();
  const profile = useQuery({ queryKey: ['company-profile'], queryFn: async () => (await api<{ data: CompanyProfile }>('GET', '/company-profile')).data });

  const [values, setValues] = useState<Texts>(empty);
  const [smallBusiness, setSmallBusiness] = useState(false);
  const [days, setDays] = useState('30');
  const [state, setState] = useState<GermanState>(DEFAULT_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const [step, setStep] = useState(0);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !profile.data) return;
    loaded.current = true;
    const p = profile.data;
    setValues(Object.fromEntries(KEYS.map((k) => [k, (p[k] as string | undefined) ?? ''])) as Texts);
    setSmallBusiness(p.smallBusiness);
    setDays(String(p.offerValidityDays));
    setState(p.state ?? DEFAULT_STATE);
  }, [profile.data]);

  const save = useMutation({
    mutationFn: async () => (await api<{ data: CompanyProfile }>('PUT', '/company-profile', { ...values, smallBusiness, offerValidityDays: Number(days), state })).data,
    onSuccess: (p) => {
      client.setQueryData(['company-profile'], p);
      client.invalidateQueries({ queryKey: ['offer-defaults'] });
      // Der Firmenname steht in der Kopfleiste (KON-115)
      client.invalidateQueries({ queryKey: ['session'] });
      setErrors({});
      setNotice({ ok: true, text: 'Die Firmendaten sind gespeichert.' });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        // Zum ersten Schritt springen, in dem ein Feld markiert ist
        const failed = STEPS.findIndex((s) => s.fields.some((f) => e.fields[f]));
        if (failed >= 0) setStep(failed);
        setNotice({ ok: false, text: 'Bitte prüfen Sie die markierten Felder.' });
      } else setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Die Firmendaten konnten nicht gespeichert werden.' });
    },
  });
  const logo = useMutation({
    mutationFn: (file: File | null) => (file ? apiUpload('/company-profile/logo', file) : api('DELETE', '/company-profile/logo')),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['company-profile'] });
      setLogoVersion((v) => v + 1);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setNotice(null);
    save.mutate();
  }

  if (profile.isLoading) return <p className="muted">Wird geladen …</p>;
  if (profile.isError) return <div className="alert alert--error">{(profile.error as Error).message}</div>;

  const field = (key: keyof Texts, label: string, props: { type?: string; max?: number; placeholder?: string; hint?: string } = {}) => (
    <label className="field">
      <span>{label}</span>
      <input className="input" type={props.type ?? 'text'} value={values[key]} onChange={(e) => setValues({ ...values, [key]: e.target.value })} maxLength={props.max} placeholder={props.placeholder} disabled={!canWrite} aria-invalid={!!errors[key]} />
      {errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : props.hint ? <small className="muted">{props.hint}</small> : null}
    </label>
  );
  const area = (key: keyof Texts, label: string, placeholder: string, rows = 3) => (
    <label className="field">
      <span>{label}</span>
      <textarea className="input textarea" rows={rows} value={values[key]} onChange={(e) => setValues({ ...values, [key]: e.target.value })} placeholder={placeholder} disabled={!canWrite} aria-invalid={!!errors[key]} />
      {errors[key] && <small className="field__error" role="alert">{errors[key]}</small>}
    </label>
  );
  const hasLogo = profile.data?.hasLogo ?? false;

  return (
    <>
    <Suspense fallback={<p className="muted">Wird geladen …</p>}><IndustryPage /></Suspense>
    <form className="card company-form" onSubmit={submit} noValidate>
      <h2>Firmendaten</h2>
      <p className="muted small">
        Aus diesen Angaben entstehen Briefkopf und Fußzeile Ihrer Angebote. Abgeschlossene Angebote behalten die Daten, die beim Abschließen galten. Pflicht ist nichts, aber
        ein Angebot ohne Anschrift, Steuernummer und Bankverbindung wirkt unvollständig.
      </p>
      {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role="status">{notice.text}</div>}

      <div className="company-steps" role="tablist" aria-label="Schritte der Firmendaten">
        {STEPS.map((s, i) => {
          const countable = s.key !== 'offers';
          const done = s.fields.filter((f) => f !== 'offerValidityDays' && values[f].trim()).length + (s.key === 'company' && hasLogo ? 1 : 0);
          const total = s.fields.length + (s.key === 'company' ? 1 : 0);
          const failed = s.fields.some((f) => errors[f]);
          return (
            <button key={s.key} type="button" role="tab" id={`company-tab-${s.key}`} aria-selected={step === i} aria-controls={`company-step-${s.key}`} className={`company-steps__tab${failed ? ' company-steps__tab--error' : ''}`} onClick={() => setStep(i)}>
              <span className="company-steps__nr">{i + 1}</span>
              <span className="company-steps__text">
                <span>{s.label}</span>
                {countable && <small className="muted">{done} von {total} ausgefüllt</small>}
              </span>
            </button>
          );
        })}
      </div>
      <p className="muted small company-steps__hint">{STEPS[step]!.hint}</p>

      <section id="company-step-company" role="tabpanel" aria-labelledby="company-tab-company" hidden={step !== 0}>
      <h3>Logo</h3>
      <div className="company-logo">
        {hasLogo ? <img src={`/api/v1/company-profile/logo?v=${logoVersion}`} alt="Ihr Logo" /> : <span className="muted small">Noch kein Logo. Ohne Logo steht der Firmenname im Briefkopf.</span>}
        {canWrite && (
          <div className="row-actions">
            <label className="btn btn--secondary btn--sm">
              {hasLogo ? 'Anderes Logo wählen' : 'Logo hochladen'}
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) logo.mutate(file);
                }}
              />
            </label>
            {hasLogo && <button type="button" className="btn btn--danger btn--sm" disabled={logo.isPending} onClick={() => window.confirm('Logo entfernen?') && logo.mutate(null)}>Entfernen</button>}
          </div>
        )}
      </div>
      <p className="muted small">PNG oder JPG, höchstens 1 MB. Am besten ein breites Bild mit transparentem oder weißem Hintergrund.</p>
      {logo.error && <div className="alert alert--error" role="alert">{logo.error.message}</div>}

      <h3>Anschrift und Kontakt</h3>
      <div className="form-grid">
        {field('companyName', 'Firmenname', { max: 160 })}
        {field('street', 'Straße und Hausnummer', { max: 120 })}
        {field('postalCode', 'PLZ', { max: 10 })}
        {field('city', 'Ort', { max: 80 })}
        {field('phone', 'Telefon', { max: 40 })}
        {field('email', 'E-Mail', { type: 'email', max: 200 })}
        {field('website', 'Webseite', { max: 200 })}
        <label className="field">
          <span>Bundesland</span>
          <select className="input" value={state} onChange={(e) => setState(e.target.value as GermanState)} disabled={!canWrite}>
            {GERMAN_STATE_CODES.map((c) => <option key={c} value={c}>{GERMAN_STATES[c]}</option>)}
          </select>
          <small className="muted">Bestimmt die Feiertage beim Urlaub. Sie zählen nicht als Urlaubstage.</small>
        </label>
      </div>
      </section>

      <section id="company-step-legal" role="tabpanel" aria-labelledby="company-tab-legal" hidden={step !== 1}>
      <h3>Rechtliche Angaben</h3>
      <div className="form-grid">
        {field('managingDirector', 'Inhaber oder Geschäftsführung', { max: 160 })}
        {field('registerCourt', 'Registergericht', { max: 120, placeholder: 'Amtsgericht Musterstadt' })}
        {field('registerNumber', 'Registernummer', { max: 60, placeholder: 'HRB 12345' })}
        {field('taxNumber', 'Steuernummer', { max: 40 })}
        {field('vatId', 'USt-IdNr.', { max: 20, placeholder: 'DE123456789' })}
      </div>
      <label className="check">
        <input type="checkbox" checked={smallBusiness} onChange={(e) => setSmallBusiness(e.target.checked)} disabled={!canWrite} />
        <span>Kleinunternehmer nach § 19 UStG. Angebote weisen dann keine Umsatzsteuer aus und tragen einen Hinweis. Das ist dieselbe Einstellung wie die Versteuerungsart in Einrichtung → Finanzen.</span>
      </label>
      </section>

      <section id="company-step-bank" role="tabpanel" aria-labelledby="company-tab-bank" hidden={step !== 2}>
      <h3>Bankverbindung</h3>
      <div className="form-grid">
        {field('bankName', 'Bank', { max: 120 })}
        {field('iban', 'IBAN', { max: 42, placeholder: 'DE89 3704 0044 0532 0130 00' })}
        {field('bic', 'BIC', { max: 11, placeholder: 'COBADEFFXXX' })}
      </div>
      {field('footerNote', 'Zusatz in der Fußzeile', { max: 500, hint: 'Zum Beispiel Öffnungszeiten. Erscheint klein unter den Firmendaten.' })}

      <h3>Steuerberater und DATEV</h3>
      <div className="form-grid">
        {field('taxAdvisorName', 'Steuerkanzlei', { max: 160 })}
        {field('taxAdvisorEmail', 'E-Mail der Kanzlei', { type: 'email', max: 200, hint: 'Hierhin geht später die Übergabe der Buchungen.' })}
        {field('datevConsultantNumber', 'Beraternummer', { max: 7, placeholder: 'bis zu 7 Ziffern', hint: FINANCE_HINTS.datevNumbers })}
        {field('datevClientNumber', 'DATEV-Mandantennummer', { max: 5, placeholder: 'bis zu 5 Ziffern' })}
      </div>
      </section>

      <section id="company-step-offers" role="tabpanel" aria-labelledby="company-tab-offers" hidden={step !== 3}>
      <h3>Vorgaben für Angebote</h3>
      <label className="field field--narrow">
        <span>Gültigkeit in Tagen</span>
        <input className="input num" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.offerValidityDays} />
        {errors.offerValidityDays ? <small className="field__error" role="alert">{errors.offerValidityDays}</small> : <small className="muted">Ein neues Angebot gilt so viele Tage ab heute. Im Angebot lässt sich das Datum ändern.</small>}
      </label>
      {area('offerIntro', 'Einleitung', DEFAULT_OFFER_TEXTS.intro)}
      {area('offerClosing', 'Schlusstext', DEFAULT_OFFER_TEXTS.closing)}
      {field('mailSubject', 'Betreff der E-Mail', { max: 200, placeholder: DEFAULT_OFFER_TEXTS.mailSubject })}
      {area('mailBody', 'Text der E-Mail', DEFAULT_OFFER_TEXTS.mailBody, 7)}
      <p className="muted small">
        Leere Felder nutzen die grau angezeigte Vorgabe. In der E-Mail setzt Wirkia diese Platzhalter ein: {'{name}'} für den Empfänger, {'{nummer}'} für die Angebotsnummer,
        {' {firma}'} für Ihren Firmennamen, {'{gueltig}'} für das Ablaufdatum und {'{link}'} für den Link zur Online-Annahme.
      </p>
      </section>

      <div className="form-actions company-steps__actions">
        {step > 0 && <button className="btn btn--secondary" type="button" onClick={() => setStep(step - 1)}>Zurück</button>}
        {step < STEPS.length - 1 && <button className="btn btn--secondary" type="button" onClick={() => setStep(step + 1)}>Weiter</button>}
        {canWrite && <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>}
      </div>
    </form>
    </>
  );
}
